import { useMemo } from 'react';
import { Badge } from 'src/components/atoms';
import {
  SearchByInputType,
  useCasesCoiPaginatedQuery,
  useCoiFilterOptionsQuery,
} from 'src/graphql';
import {
  DefaultSearchOptionsType,
  useTableColumns,
} from 'src/hooks/table/useTableColumns';
import { CustomFieldFilterSelection } from './types';
import { getReadableIdWithDash } from 'src/types';
import { DateFormats, formatDate } from 'src/utils/time';

const EMPTY_SEARCH_OPTIONS: DefaultSearchOptionsType[] = [];
const EMPTY_APPLIED_FILTERS = {};

const columns = [
  { label: 'Case ID', value: 'caseId' },
  { label: 'Form Title', value: 'formTitle' },
  { label: 'Category', value: 'category' },
  { label: 'Employee', value: 'employee' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Status', value: 'status' },
  { label: 'Flags', value: 'flags' },
  { label: 'Reviewer', value: 'reviewer' },
];

const sortableColumns = [
  'Form Title',
  'Category',
  'Employee',
  'Submitted',
  'Status',
  'Flags',
  'Reviewer',
];

export const getPageOffset = (page: number, rows: number) =>
  Math.max(0, (page - 1) * rows);

export const getTotalPages = (count: number, rows: number) =>
  Math.ceil(count / rows) || 1;

interface CoiFilters {
  status?: string[];
  reviewer?: string[];
  submittedDate?: {
    from: string;
    to: string;
  };
  formTitle?: string[];
  category?: string[];
  textFilterLogic?: 'and' | 'or';
  customField?: CustomFieldFilterSelection;
}

export const useFlaggedCasesTable = (
  rows: number,
  page: number,
  filters?: CoiFilters,
  redirect?: (url: string) => void,
  searchTerm?: string,
) => {
  const { visibleColumns, sortedColumn, sortColumns, updateVisibleColumns } =
    useTableColumns(
      rows,
      page,
      columns,
      [],
      'createdAt',
      'desc',
      EMPTY_SEARCH_OPTIONS,
      EMPTY_APPLIED_FILTERS,
    );

  const orderBy = useMemo(() => {
    if (sortedColumn.columnName) {
      const columnValue = columns.find(
        (c) => c.label === sortedColumn.columnName,
      )?.value;
      if (columnValue) {
        const sortByMap: Record<string, string> = {
          formTitle: 'name',
          employee: 'name',
          submitted: 'createdAt',
          status: 'status',
        };
        const fieldName = sortByMap[columnValue] || 'createdAt';
        return {
          fieldName,
          direction: sortedColumn.direction === 'asc' ? 'ASC' : 'DESC',
        };
      }
    }
    return {
      fieldName: 'createdAt',
      direction: 'DESC' as const,
    };
  }, [sortedColumn]);

  const pagination = useMemo(
    () => ({ limit: rows, offset: getPageOffset(page, rows) }),
    [rows, page],
  );

  const searchBy = useMemo(() => {
    const searchByArray: SearchByInputType[] = [];

    if (filters?.status?.length) {
      searchByArray.push({
        fieldName: 'Case.status',
        fieldValue: filters.status.join(','),
      });
    }

    if (filters?.reviewer?.length) {
      const allNumeric = filters.reviewer.every((r) => /^\d+$/.test(r));
      searchByArray.push({
        fieldName: allNumeric ? 'CaseLead.id' : 'CaseLead.name',
        fieldValue: filters.reviewer.join(','),
      });
    }

    if (filters?.submittedDate) {
      let submittedFrom = formatDate(
        filters.submittedDate.from,
        DateFormats.YYYY_MM_DD,
        true,
        undefined,
      );
      let submittedTo = formatDate(
        filters.submittedDate.to,
        DateFormats.YYYY_MM_DD,
        true,
        undefined,
      );

      if (submittedFrom && submittedTo) {
        if (submittedFrom > submittedTo) {
          // Swap if from date is greater than to date
          [submittedFrom, submittedTo] = [submittedTo, submittedFrom];
        }
        searchByArray.push({
          fieldName: 'companyCreatedAt',
          fieldValue: `${submittedFrom},${submittedTo}`,
        });
      }
    }

    if (filters?.formTitle?.length) {
      searchByArray.push({
        fieldName: 'Case.name',
        fieldValue: filters.formTitle.join(','),
      });
    }

    if (filters?.category?.length) {
      searchByArray.push({
        fieldName: 'Categories.locales',
        fieldValue: filters.category.join(','),
      });
    }

    return searchByArray.length > 0 ? searchByArray : undefined;
  }, [filters]);

  const fuzzy = useMemo(
    () =>
      searchTerm?.trim()
        ? { value: searchTerm.trim().toLowerCase() }
        : undefined,
    [searchTerm],
  );

  const customFieldSearch = useMemo(() => {
    const selection = filters?.customField;
    if (!selection?.fieldId || !selection.value.trim()) {
      return undefined;
    }
    return { fieldId: selection.fieldId, value: selection.value.trim() };
  }, [filters?.customField]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch: refetchCases,
  } = useCasesCoiPaginatedQuery(
    {
      orderBy,
      pagination,
      searchBy,
      fuzzy,
      customFieldSearch,
    },
    {
      enabled: true,
    },
  );

  const cases = data?.casesCoiPaginated?.items ?? [];

  const tableData = cases.map((caseItem) => {
    const canViewCaseDetails = Boolean(
      caseItem.AssociatedPermissions?.canViewCaseDetails,
    );
    const firstReport = caseItem.Reports?.[0];
    const formTitle = caseItem.name;
    const category =
      firstReport?.DeterminedCategory?.name ??
      firstReport?.ReportedCategory?.name ??
      caseItem.Categories?.[0]?.name ??
      '';
    const employee = firstReport?.Reporter?.name ?? '';
    const reviewer = caseItem.CaseLead?.name ?? '-';
    const reviewerGroup =
      caseItem.CaseTeam?.filter((m) => m.id !== caseItem.CaseLead?.id)
        ?.map((m) => m?.name)
        ?.filter((name): name is string => Boolean(name?.trim())) ?? [];
    const submitted = formatDate(
      caseItem.CaseAttributes?.companyCreatedAt ?? null,
      DateFormats.DAY_MONTH_YEAR,
      true,
      '-',
    );
    const status = (
      <Badge key={caseItem.id} variant="new" text={caseItem.status ?? '-'} />
    );
    const flags = caseItem.DisclosuresCaseData?.flags ?? 0;

    return {
      cellData: [
        canViewCaseDetails
          ? getReadableIdWithDash(
              caseItem.DisclosuresCaseData?.responseId?.toLocaleUpperCase(),
            )
          : '-',
        canViewCaseDetails ? formTitle : '-',
        canViewCaseDetails ? category : '-',
        canViewCaseDetails ? employee : '-',
        canViewCaseDetails ? submitted : '-',
        canViewCaseDetails ? status : '-',
        canViewCaseDetails ? flags : '-',
        canViewCaseDetails ? reviewer : '-',
        canViewCaseDetails ? reviewerGroup : '-',
      ],
      row: caseItem.id ?? '',
      data: {
        id: caseItem.id ?? '',
        status: caseItem.status ?? '',
        reportId: caseItem.Reports?.[0]?.id,
        existingReport: caseItem.Reports?.[0]?.existingReport,
      },
    };
  });

  const onRowClick = redirect
    ? (caseId: string) => {
        if (caseId) {
          redirect(`/disclosure?id=${caseId}`);
        }
      }
    : undefined;

  const count = data?.casesCoiPaginated?.count ?? 0;
  const totalPages = getTotalPages(count, rows);

  const { data: filterOptionsData } = useCoiFilterOptionsQuery(undefined, {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const statusOptions = filterOptionsData?.coiFilterOptions?.statuses ?? [];
  const reviewerOptions = filterOptionsData?.coiFilterOptions?.reviewers ?? [];
  const formTitleOptions =
    filterOptionsData?.coiFilterOptions?.formTitles ?? [];
  const categoryOptions = filterOptionsData?.coiFilterOptions?.categories ?? [];
  const customFieldOptions =
    filterOptionsData?.coiFilterOptions?.customFields ?? [];

  return {
    columns,
    sortableColumns,
    visibleColumns,
    columnInfoTooltips: [],
    updateVisibleColumns,
    tableData,
    isLoading,
    isFetching,
    isError,
    sortColumns,
    sortedColumn,
    count,
    totalPages,
    refetchFormResponses: refetchCases,
    statusOptions,
    reviewerOptions,
    onRowClick,
    formTitleOptions,
    categoryOptions,
    customFieldOptions,
  };
};
